"use client"

import { createContext, useContext, useState, useEffect } from "react"
import { getDatabase, ref, onValue } from "firebase/database"

const CountContext = createContext()

export const useCount = () => {
  const context = useContext(CountContext)
  if (!context) {
    throw new Error("useCount must be used within a CountProvider")
  }
  return context
}

export const CountProvider = ({ children, selectedBrand, adminView }) => {
  const [counts, setCounts] = useState({
    userOrders: 0,
    userQueries: 0,
    userQuotations: 0,
    userSteelOrders: 0,
    userMachineryOrders: 0,
    finance: 0,
    transportGoods: 0,
    chatbot: 0,
  })

  const updateCount = (componentName, count) => {
    setCounts((prev) => ({
      ...prev,
      [componentName]: count,
    }))
  }

  // Fetch counts from Firebase
  useEffect(() => {
    if (!selectedBrand && !adminView) return

    const db = getDatabase()

    // Fetch User Orders Count (Cement Orders)
    const ordersRef = ref(db, "orders")
    const unsubscribeOrders = onValue(ordersRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val()
        let count = 0

        Object.keys(data).forEach((userId) => {
          Object.keys(data[userId]).forEach((orderId) => {
            const order = data[userId][orderId]
            const isCementOrder = !order.category || order.category === "Cement" || order.category === "Bulker Cement"

            let brandMatch = false
            if (adminView && !selectedBrand) {
              brandMatch = true
            } else {
              brandMatch = order.brand === selectedBrand
            }

            // Count pending orders
            if (isCementOrder && brandMatch && order.status === "Pending") {
              count++
            }
          })
        })

        updateCount("userOrders", count)
      }
    })

    // Fetch User Queries Count
    const queriesRef = ref(db, "queries")
    const unsubscribeQueries = onValue(queriesRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val()
        let count = 0

        Object.keys(data).forEach((userId) => {
          Object.keys(data[userId]).forEach((queryId) => {
            const query = data[userId][queryId]
            const hasNoReply = !query.supplierReply || Object.keys(query.supplierReply).length === 0

            let brandMatch = false
            if (query.category === "Cement" || !query.category) {
              brandMatch = adminView && !selectedBrand ? true : query.brand === selectedBrand
            } else if (query.category === "Steel") {
              brandMatch = adminView ? true : query.steelType === selectedBrand
            } else if (query.category === "Machinery") {
              brandMatch = adminView ? true : query.machineryType === selectedBrand
            }else if (query.category === "Finance") {
                    brandMatch = adminView ? true : query.financeType === selectedBrand
                  }

            if (hasNoReply && brandMatch && query.companyName) {
              count++
            }
          })
        })

        updateCount("userQueries", count)
      }
    })

    // Fetch User Quotations Count
    const quotationsRef = ref(db, "quoteRequests")
    const unsubscribeQuotations = onValue(quotationsRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val()
        let count = 0

        Object.keys(data).forEach((userId) => {
          Object.keys(data[userId]).forEach((quoteId) => {
            const quote = data[userId][quoteId]
            const hasNoReply = !quote.supplierReply

            let brandMatch = false
            if (quote.category === "cement" || !quote.category) {
              brandMatch = adminView && !selectedBrand ? true : quote.brand === selectedBrand
            } else if (quote.category === "steel") {
              brandMatch = adminView ? true : quote.steelType === selectedBrand
            } else if (quote.category === "machinery") {
              brandMatch = adminView ? true : quote.machineryType === selectedBrand
            }

            if (hasNoReply && brandMatch && quote.companyName) {
              count++
            }
          })
        })

        updateCount("userQuotations", count)
      }
    })

// 🔹 Define helper function
const listenUnreadChats = (updateCount) => {
  const db = getDatabase()
  const chatsRef = ref(db, "userchatbox")

  // Firebase `onValue` itself returns the unsubscribe function
  const unsubscribeChats = onValue(chatsRef, (snapshot) => {
    if (snapshot.exists()) {
      const data = snapshot.val()
      let count = 0

      Object.keys(data).forEach((userId) => {
        const userChats = data[userId]

        Object.keys(userChats).forEach((chatId) => {
          if (chatId.startsWith("chat-")) {
            const messages = userChats[chatId]

            Object.keys(messages).forEach((msgId) => {
              const msg = messages[msgId]
              if (msg?.sender === "user" && !msg?.read) {
                count++
              }
            })
          }
        })
      })

      updateCount("chatbot", count)
    } else {
      updateCount("chatbot", 0)
    }
  })

  return unsubscribeChats 
}



const listenRefundCounts = (updateCount) => {
  const db = getDatabase()
  const refundsRef = ref(db, "refunds")

  const unsubscribeRefunds = onValue(refundsRef, (snapshot) => {
    if (snapshot.exists()) {
      const data = snapshot.val()
      let count = 0

      // Loop through all users
      Object.entries(data).forEach(([userId, refundsData]) => {
        // Loop through each refund entry
        Object.entries(refundsData).forEach(([refundId, refundDetails]) => {
          // Only count unresolved refunds
          if (!refundDetails.resolved) {
            count++
          }
        })
      })

      updateCount("refunds", count)
    } else {
      updateCount("refunds", 0)
    }
  })

  return unsubscribeRefunds
}


    // Fetch Steel Orders Count
 const steelOrdersRef = ref(db, "steelOrder") // ✅ Confirmed path
const unsubscribeSteelOrders = onValue(steelOrdersRef, (snapshot) => {
  if (snapshot.exists()) {
    const data = snapshot.val()
    let count = 0

    Object.keys(data).forEach((userId) => {
      Object.keys(data[userId]).forEach((orderId) => {
        const order = data[userId][orderId]

        let brandMatch = false
        if (adminView && !selectedBrand) {
          brandMatch = true
        } else {
          brandMatch = order.steelType === selectedBrand
        }

        if (brandMatch && order.status === "Pending") {
          count++
        }
      })
    })

    console.log("Steel Orders Pending Count:", count) // ✅ Debug
    updateCount("userSteelOrders", count)
  } else {
    updateCount("userSteelOrders", 0)
  }
})


    // Fetch Machinery Orders Count
const machineryOrdersRef = ref(db, "machineryOrder") // ✅ MATCHING path

const unsubscribeMachineryOrders = onValue(machineryOrdersRef, (snapshot) => {
  if (snapshot.exists()) {
    const data = snapshot.val()
    let count = 0

    Object.keys(data).forEach((userId) => {
      Object.keys(data[userId]).forEach((orderId) => {
        const order = data[userId][orderId]

        let brandMatch = false
        if (adminView && !selectedBrand) {
          brandMatch = true
        } else {
          brandMatch = order.machineryType === selectedBrand
        }

        if (brandMatch && order.status === "Pending") {
          count++
        }
      })
    })

    updateCount("userMachineryOrders", count)
  } else {
    updateCount("userMachineryOrders", 0) // fallback
  }
})

const transportRef = ref(db, "transport_goods")

const unsubscribeTransportGoods = onValue(transportRef, (snapshot) => {
  if (snapshot.exists()) {
    const data = snapshot.val()
    let count = 0

    Object.entries(data).forEach(([userId, transports]) => {
      Object.entries(transports).forEach(([transportId, details]) => {
        // ✅ Safely check if selectedBrand response exists
        const alreadyResponded =
          details?.[`${selectedBrand}Response`] !== undefined

        // ✅ Count only if not responded
        if (!alreadyResponded) {
          count++
        }
      })
    })

    // ✅ Update your context or state
    updateCount("transportGoods", count)
  } else {
    updateCount("transportGoods", 0) // fallback when no data
  }
})


    // Fetch Rate Requests Count
    const rateRequestsRef = ref(db, "rateRequest")
    const unsubscribeRateRequests = onValue(rateRequestsRef, (snapshot) => {
      let count = 0
      if (snapshot.exists()) {
        const data = snapshot.val()
        Object.keys(data).forEach((userId) => {
          Object.values(data[userId]).forEach((request) => {
            if (!request.rate && !request.seen) {
              count++
            }
          })
        })
      }
      updateCount("currentRates", count)
    })

    const financeRef = ref(db, "Finance");

const unsubscribeFinance = onValue(financeRef, (snapshot) => {
  if (snapshot.exists()) {
    const data = snapshot.val();
    let count = 0;

    Object.keys(data).forEach((userId) => {
      if (typeof data[userId] === "object") {
        Object.keys(data[userId]).forEach((applicationId) => {
          const applicationData = data[userId][applicationId];
          if (applicationData && applicationData.personalInfo) {
            count++;
          }
        });
      }
    });

    updateCount("finance", count); // use the key 'finance' in your counts object
  } else {
    updateCount("finance", 0);
  }
});

 const unsubscribeChats = listenUnreadChats(updateCount)
 const unsubscribeRefunds = listenRefundCounts(updateCount)
  

    // Cleanup function
    return () => {
      unsubscribeOrders()
      unsubscribeQueries()
      unsubscribeQuotations()
      unsubscribeSteelOrders()
      unsubscribeMachineryOrders()
      unsubscribeRateRequests()
      unsubscribeTransportGoods()
      unsubscribeFinance()
      unsubscribeChats()
      unsubscribeRefunds()
    }
  }, [selectedBrand, adminView])

  return <CountContext.Provider value={{ counts, updateCount }}>{children}</CountContext.Provider>
}
